import styles from './statistics-styles.module.scss';

import { Theme, useMediaQuery } from '@mui/material';
import { ColumnDef, useReactTable } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/table-core';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StatisticSalesVolumeBySupplier } from '@work4all/models/lib/Classes/StatisticSalesVolumeBySupplier.entity';
import { StatisticsSalesVolumeBySupplierColumn } from '@work4all/models/lib/Classes/StatisticsSalesVolumeBySupplierColumn.entity';
import { StatisticsSalesVolumeBySupplierRow } from '@work4all/models/lib/Classes/StatisticsSalesVolumeBySupplierRow.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';

import { ICssClasses } from '../types';
import { CurrencyCell } from '../utils';
import { useDefaultReactTableOptions } from '../utils/use-default-react-table-options';
import { VirtualizedTableV8 } from '../VirtualizedTableV8';

import { getStatisticFormatedDate } from './getStatisticFormatedDate';

interface StatisticSalesVolumeBySupplierTableProps {
  data: StatisticSalesVolumeBySupplier;
  tableClasses?: ICssClasses;
}

interface MonthDefinition {
  amount: number;
  date: string;
}

interface StatisticItemRemodeled {
  supplier: Supplier;
  sum: number;
  months: MonthDefinition[];
}

const GetTotalSum = (
  months: StatisticsSalesVolumeBySupplierColumn[],
  rowIndex: number
) => {
  let result = 0;
  months.forEach((column) => {
    result += column.rows?.[rowIndex].amount;
  });

  return result;
};

const GetTotalMonthValue = (data: StatisticItemRemodeled[], month: number) => {
  let result = 0;

  data.forEach((row) => {
    result += row.months[month].amount;
  });

  return (
    <CurrencyCell
      className={clsx({ [styles.red]: result < 0 })}
      value={result}
      maximumFractionDigits={0}
      minimumFractionDigits={0}
      hideUnit={false}
    />
  );
};

const GetTotalTotalSumValue = (data: StatisticItemRemodeled[]) => {
  let result = 0;

  data.forEach((row) => {
    result += row.sum;
  });

  return (
    <CurrencyCell
      className={clsx({ [styles.red]: result < 0 })}
      value={result}
      maximumFractionDigits={0}
      minimumFractionDigits={0}
      hideUnit={false}
    />
  );
};

export const StatisticSalesVolumeBySupplierTable: React.FC<
  StatisticSalesVolumeBySupplierTableProps
> = (props) => {
  const { data, tableClasses } = props;

  const isViewportDownMd = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const { t } = useTranslation();

  const newData: StatisticItemRemodeled[] = useMemo(() => {
    let result: StatisticItemRemodeled[] = [];

    if (data.columns === undefined) {
      return [];
    }
    const months = [...data.columns].sort((a, b) => {
      if (new Date(a.rangeStart) > new Date(b.rangeStart)) {
        return 1;
      }
      if (new Date(a.rangeStart) < new Date(b.rangeStart)) {
        return -1;
      }
      return 0;
    });

    months?.[0].rows.forEach(
      (row: StatisticsSalesVolumeBySupplierRow, rowIndex) => {
        const newItem: StatisticItemRemodeled = {
          supplier: row.lieferant,
          sum: GetTotalSum(months, rowIndex),
          months: months.map((col) => {
            return {
              amount: col.rows?.[rowIndex].amount,
              date: col.rows?.[rowIndex].date,
            };
          }),
        };
        result.push(newItem);
      }
    );

    result = result.sort((a, b) => {
      if (a.sum > b.sum) {
        return -1;
      }
      if (a.sum < b.sum) {
        return 1;
      }
      return 0;
    });

    return result;
  }, [data]);

  const columns = useMemo<ColumnDef<StatisticItemRemodeled, number>[]>(() => {
    const defaultProperties: Partial<
      ColumnDef<StatisticItemRemodeled, number>
    > = {
      size: isViewportDownMd ? 100 : 150,
      cell: (props: { getValue: () => number }) => {
        const value = props.getValue();
        if (value === 0) {
          return '';
        }
        return (
          <CurrencyCell
            className={clsx({ [styles.red]: value < 0 })}
            value={value}
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            hideUnit={true}
          />
        );
      },
      alignment: 'right',
    };

    let result: ColumnDef<StatisticItemRemodeled, number>[] = [
      {
        header: t('COMMON.SUPPLIER'),
        accessorKey: 'supplier.name',
        size: isViewportDownMd ? 150 : 300,
      },

      {
        ...defaultProperties,
        header: t('COMMON.SUM'),
        accessorKey: 'sum',
        footer: () => GetTotalTotalSumValue(newData),
        cell: (props: { getValue: () => number }) => {
          const value = props.getValue();
          if (value === 0) {
            return '';
          }
          return (
            <CurrencyCell
              className={clsx({ [styles.red]: value < 0 })}
              value={value}
              maximumFractionDigits={0}
              minimumFractionDigits={0}
              hideUnit={true}
            />
          );
        },
      },
    ];

    if (newData[0] !== undefined) {
      const monthColumns = newData?.[0].months.map((month, monthIndex) => {
        return {
          ...defaultProperties,
          header: getStatisticFormatedDate(new Date(month.date), 'MMM yyyy'),
          accessorFn: (row: StatisticItemRemodeled) =>
            row.months[monthIndex]?.amount,
          id: `months_${monthIndex}_amount`,
          footer: () => GetTotalMonthValue(newData, monthIndex),
        } as ColumnDef<StatisticItemRemodeled, number>;
      });

      result = [...result, ...monthColumns];
    }

    return result;
  }, [newData, t, isViewportDownMd]);

  const table = useReactTable<StatisticItemRemodeled>({
    ...useDefaultReactTableOptions(),
    getSortedRowModel: getSortedRowModel(),
    columns,
    data: newData,
    enableMultiRowSelection: false,
    enableColumnResizing: false,
    entityType: null,
    initialState: {
      columnPinning: { left: ['supplier.name'] },
    },
  });

  return (
    <VirtualizedTableV8
      table={table}
      fetchMore={() => null}
      classes={tableClasses}
      className={styles.table}
      displayFooter
    />
  );
};
